import * as Yup from 'yup';

import { formatDate } from 'utils/date';
import {
  getUploadedFile,
  makeSelectOptionsFromOptionsArray,
} from 'utils/helpers';
import {
  phoneNumber,
  validateZipCode,
  validateWebsiteUrl,
} from 'utils/validation';
import {
  EventType,
  EventTypeTitle,
  EventLocationType,
  EventLocationTypeTitle,
} from 'types';
import {
  Event,
  EventFormValues,
  eventPaymentTypeLabel,
  eventPaymentTypeOptions,
  eventPaymentTransactionLabel,
  eventPaymentTransactionOptions,
} from 'store/api/event';

const required = 'Required field';

export const validationSchema = Yup.object().shape({
  zip: validateZipCode(),
  link: validateWebsiteUrl,
  name: Yup.string().required(required),
  address: Yup.string().required(required),
  contactPhone: phoneNumber('contactPhone'),
  isManualCreditPaymentEnabled: Yup.boolean(),
  tax: Yup.number().min(0).max(100).required(),
  floorPlanLink: Yup.string().url('Must be a valid url').nullable(),
  payments: Yup.array().of(
    Yup.object().shape({
      type: Yup.string().required(required),
    })
  ),
  locations: Yup.array()
    .of(
      Yup.object().shape({
        number: Yup.string().required('Number is required'),
        eventType: Yup.string().required('Event type is required'),
        locationType: Yup.string().required('Location type is required'),
      })
    )
    .min(1),
  weeklySchedule: Yup.array().of(
    Yup.object().shape({
      to: Yup.number(),
      from: Yup.number().test(
        'from-less-than-to',
        'The opening time cannot be longer than the closing time',
        function (value?: number) {
          const { to } = this.parent;

          if (!to && !value) {
            return true;
          }

          return Number(value) <= to;
        }
      ),
    })
  ),
});

const weekDays = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

export const getDefaultWeeklySchedule = () =>
  weekDays.map((day) => ({
    day,
    openingTime: null,
    closingTime: null,
  }));

export const getInitialValues = (data: Event | undefined): EventFormValues => {
  const { theme, product, ...restData } = (data ?? {}) as Event;

  return {
    ...restData,
    themeId: theme?.id ?? null,
    name: restData?.name ?? '',
    tax: data?.tax ?? undefined,
    productId: product?.id ?? null,
    address: restData?.address ?? '',
    link: restData?.link ?? undefined,
    photo: getUploadedFile(restData?.photoUrl),
    weeklySchedule: data?.weeklySchedule ?? getDefaultWeeklySchedule(),
    endDate: restData?.endDate ? formatDate(restData.endDate) : undefined,
    isManualCreditPaymentEnabled: data?.isManualCreditPaymentEnabled ?? false,
    startDate: restData?.startDate ? formatDate(restData.startDate) : undefined,
    teamMembers: restData?.teamMembers
      ? restData.teamMembers.map((user) => user.id)
      : [],
    payments: restData?.payments
      ? restData.payments.map((item) => ({
          ...item,
          date: item.date ? new Date(item.date) : undefined,
        }))
      : [],
    locations: restData?.locations?.length
      ? restData.locations.map((location) => ({
          ...location,
          number: location?.number ?? undefined,
          eventType: location?.eventType ?? undefined,
          locationType: location?.locationType ?? undefined,
        }))
      : [
          {
            number: undefined,
            eventType: undefined,
            locationType: undefined,
          },
        ],
  };
};

export const paymentTypeOptions = makeSelectOptionsFromOptionsArray(
  eventPaymentTypeOptions,
  eventPaymentTypeLabel
);

export const transactionOptions = makeSelectOptionsFromOptionsArray(
  eventPaymentTransactionOptions,
  eventPaymentTransactionLabel
);

export const eventLocationTypeOptions = Object.values(EventLocationType).map(
  (item) => ({
    value: item,
    label: EventLocationTypeTitle[item],
  })
);

export const eventTypeOptions = Object.values(EventType).map((item) => ({
  value: item,
  label: EventTypeTitle[item],
}));
